import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BASE_PATH = '/api/v1/prop-tool';

const getAuthHeaders = () => {
    const token = authService.getToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

const handleApiError = (error) => {
    if (error.response) {
        const { status, data } = error.response;

        if (status === 401) {
            return {
                success: false,
                status,
                errorCode: data.error || 'UNAUTHORIZED',
                message: data.message || 'Authentication failed',
                isAuthError: true
            };
        }

        if (status === 403) {
            return {
                success: false,
                status,
                errorCode: data.error || 'SUBSCRIPTION_REQUIRED',
                message: data.message || 'A Snipe Pro or Snipe Elite subscription is required to access the prop tool. Please upgrade your plan.',
                isSubscriptionError: true
            };
        }

        if (status === 400) {
            return {
                success: false,
                status,
                errorCode: data.error || 'VALIDATION_ERROR',
                message: data.message || 'Invalid request parameters',
                details: data.errors || []
            };
        }

        if (status === 404) {
            return {
                success: false,
                status,
                errorCode: data.error || 'PLAYER_NOT_FOUND',
                message: data.message || 'No player found for the requested id.',
                isNotFound: true
            };
        }

        if (status === 422) {
            return {
                success: false,
                status,
                errorCode: data.error || 'UNPROCESSABLE',
                message: data.message || 'Invalid filter combination.',
                details: data.errors || []
            };
        }

        if (status === 429) {
            return {
                success: false,
                status,
                errorCode: data.error || 'RATE_LIMIT_EXCEEDED',
                message: data.message || 'Too many prop tool requests. Please try again in a minute.'
            };
        }

        return {
            success: false,
            status,
            errorCode: data.error || 'SERVER_ERROR',
            message: data.message || 'Server error. Please try again later.'
        };
    }

    return {
        success: false,
        status: null,
        errorCode: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.'
    };
};

const propToolService = {
    // Fetch a player's historical game-by-game stats. `seasons` is optional and
    // may be an array of 4-digit years or a comma-separated string; omit for all.
    // `without` is an optional list of same-team teammate player_ids — only games
    // where none of them played are returned. `withPlayers` is the inverse — only
    // games where the player and all of them played together are returned.
    getAflPlayerStats: async (playerId, seasons, without, withPlayers) => {
        try {
            const params = { player_id: playerId };
            if (seasons) {
                params.season = Array.isArray(seasons) ? seasons.join(',') : seasons;
            }
            if (without && (Array.isArray(without) ? without.length : without)) {
                params.without = Array.isArray(without) ? without.join(',') : without;
            }
            if (withPlayers && (Array.isArray(withPlayers) ? withPlayers.length : withPlayers)) {
                params.with = Array.isArray(withPlayers) ? withPlayers.join(',') : withPlayers;
            }

            const response = await axios.get(`${API_URL}${BASE_PATH}/afl/player_stats`, {
                params,
                headers: getAuthHeaders()
            });

            const result = response.data;

            if (!result.success) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch player stats'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    // Fetch Centre Bounce Attendance grids for one or more teams (full club
    // names as they appear on games, e.g. "Adelaide Crows"). Returns one grid
    // per (team, season), pre-pivoted: rows are players, columns are rounds.
    getAflTeamCba: async (teams) => {
        try {
            const list = Array.isArray(teams) ? teams : [teams];
            const params = { teams: list.filter(Boolean).join(',') };

            const response = await axios.get(`${API_URL}${BASE_PATH}/afl/cba`, {
                params,
                headers: getAuthHeaders()
            });

            const result = response.data;

            if (!result.success) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch CBA data'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    // Fetch Ruck Contest grids for one or more teams. Mirrors the CBA endpoint
    // exactly (same params/validations/shape); the per-round map key is
    // `ruck_contests_by_round` instead of `cba_by_round`.
    getAflTeamRuckContests: async (teams) => {
        try {
            const list = Array.isArray(teams) ? teams : [teams];
            const params = { teams: list.filter(Boolean).join(',') };

            const response = await axios.get(`${API_URL}${BASE_PATH}/afl/ruck-contests`, {
                params,
                headers: getAuthHeaders()
            });

            const result = response.data;

            if (!result.success) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch ruck contest data'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    // Fetch Kick-In grids for one or more teams. Mirrors the CBA/ruck endpoints
    // (same params/validations/shape); each player row carries two per-round
    // maps: `kick_ins_by_round` (count — the primary value) and
    // `kick_ins_play_on_by_round` (play-on %).
    getAflTeamKickIns: async (teams) => {
        try {
            const list = Array.isArray(teams) ? teams : [teams];
            const params = { teams: list.filter(Boolean).join(',') };

            const response = await axios.get(`${API_URL}${BASE_PATH}/afl/kick-ins`, {
                params,
                headers: getAuthHeaders()
            });

            const result = response.data;

            if (!result.success) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch kick-in data'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    }
};

export default propToolService;
